import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { blogService } from '../services/blogService';

gsap.registerPlugin(ScrollTrigger);

const BlogListPage = () => {
    const [blogs, setBlogs] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');

    // Icon rendering helper
    const renderIcon = (iconName, size = 24) => {
        const icons = {
            book: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
            edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
            palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
            lightbulb: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
            trophy: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>,
            phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
            mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
            mapPin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
            message: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
            inbox: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
        };
        return icons[iconName] || icons.book;
    };

    useEffect(() => {
        blogService.getAllBlogs()
            .then(data => {
                setBlogs(data);
                setFilteredBlogs(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching blogs:', err);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        // Hero animations
        gsap.fromTo('.blog-hero-title',
            {
                y: 60,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1.2,
                ease: 'power4.out'
            }
        );

        gsap.fromTo('.blog-hero-subtitle',
            {
                y: 40,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                delay: 0.3,
                ease: 'power3.out'
            }
        );

        // Section header animations
        gsap.fromTo('.blog-section-title',
            {
                y: 40,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                scrollTrigger: {
                    trigger: '.blog-section-title',
                    start: 'top 85%',
                },
                duration: 0.8,
                ease: 'power3.out'
            }
        );

        gsap.fromTo('.blog-section-subtitle',
            {
                y: 30,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                scrollTrigger: {
                    trigger: '.blog-section-subtitle',
                    start: 'top 85%',
                },
                duration: 0.8,
                delay: 0.2,
                ease: 'power3.out'
            }
        );

        // Filter buttons animation
        gsap.fromTo('.category-filter-btn',
            {
                y: 20,
                opacity: 0,
                scale: 0.9
            },
            {
                y: 0,
                opacity: 1,
                scale: 1,
                scrollTrigger: {
                    trigger: '.category-filter-btn',
                    start: 'top 85%',
                },
                duration: 0.5,
                stagger: 0.08,
                delay: 0.4,
                ease: 'back.out(1.7)'
            }
        );

        // Blog cards animation
        gsap.utils.toArray('.blog-card').forEach((card, index) => {
            gsap.fromTo(card,
                {
                    y: 80,
                    opacity: 0,
                    scale: 0.9,
                    rotateX: 15
                },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    rotateX: 0,
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                    },
                    duration: 0.8,
                    delay: (index % 3) * 0.15,
                    ease: 'power3.out'
                }
            );
        });

        // Newsletter section animations
        gsap.fromTo('.newsletter-content',
            {
                y: 60,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                scrollTrigger: {
                    trigger: '.newsletter-content',
                    start: 'top 80%',
                },
                duration: 1,
                ease: 'power3.out'
            }
        );

        // Stats animation
        gsap.utils.toArray('.blog-stat-item').forEach((stat, index) => {
            gsap.fromTo(stat,
                {
                    y: 40,
                    opacity: 0,
                    scale: 0.9
                },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    scrollTrigger: {
                        trigger: stat,
                        start: 'top 85%',
                    },
                    duration: 0.6,
                    delay: index * 0.15,
                    ease: 'back.out(1.7)'
                }
            );
        });

        // Parallax effect for hero
        gsap.to('.hero', {
            backgroundPosition: '50% 100%',
            ease: 'none',
            scrollTrigger: {
                trigger: '.hero',
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }, [filteredBlogs]);

    const handleCategoryFilter = (category) => {
        setActiveCategory(category);
        if (category === 'all') {
            setFilteredBlogs(blogs);
        } else {
            setFilteredBlogs(blogs.filter(blog => blog.category === category));
        }
    };

    const categories = ['all', ...new Set(blogs.map(blog => blog.category))];

    return (
        <div>
            <Helmet>
                <title>Blog - Hav'n Interiors</title>
                <meta name="description" content="Read the latest insights, trends, and inspiration for interior design from Hav'n Interiors experts." />
                <meta name="keywords" content="interior design blog, design trends, home decor ideas, luxury interiors" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="hero" style={{ 
                height: '60vh',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=1920&q=90)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: window.innerWidth > 768 ? 'fixed' : 'scroll',
                paddingTop: '120px'
            }}>
                <div className="hero-content" style={{ position: 'relative', zIndex: 2, textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)' }}>
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ 
                            fontSize: '14px',
                            color: 'var(--accent-color)',
                            letterSpacing: '3px',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                        }}>
                            Design Knowledge
                        </span>
                    </div>
                    <h1 className="blog-hero-title" style={{ fontSize: 'clamp(2rem, 8vw, 4rem)', fontWeight: '300', color: '#F5F1ED', marginBottom: '20px' }}>
                        Our Blog
                    </h1>
                    <p className="blog-hero-subtitle hero-subtitle" style={{ fontSize:'clamp(1rem,4vw,1.4rem)', color: '#F5F1ED', fontWeight: '400' }}>
                        Design Insights & Inspiration from Industry Experts
                    </p>
                </div>
            </section>

            {/* Blog Content with Filters */}
            <section style={{ 
                background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 50%, #1A1A1A 100%)', 
                padding: '80px 5% 100px',
                position: 'relative'
            }}>
                {/* Decorative Background Elements */}
                <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at 20% 30%, rgba(201, 169, 110, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(201, 169, 110, 0.03) 0%, transparent 50%)',
                    pointerEvents: 'none'
                }}></div>

                <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    {/* Section Header */}
                    <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '60px',
                        paddingBottom: '40px',
                        borderBottom: '1px solid rgba(201, 169, 110, 0.1)'
                    }}>
                        <h2 className="blog-section-title" style={{
                            fontSize: 'clamp(28px, 4vw, 42px)',
                            fontWeight: '300',
                            color: 'var(--text-light)',
                            marginBottom: '15px',
                            letterSpacing: '-0.5px'
                        }}>
                            Explore Our <span style={{ color: 'var(--accent-color)' }}>Design Articles</span>
                        </h2>
                        <p className="blog-section-subtitle" style={{
                            fontSize: '17px',
                            color: 'rgba(245, 241, 237, 0.6)',
                            maxWidth: '600px',
                            margin: '0 auto 40px',
                            lineHeight: '1.6'
                        }}>
                            Discover insights, trends, and inspiration from our design experts
                        </p>

                        {/* Category Filters */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '12px', 
                            justifyContent: 'center', 
                            flexWrap: 'wrap',
                            marginTop: '35px'
                        }}>
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => handleCategoryFilter(category)}
                                    className="category-filter-btn"
                                    style={{
                                        padding:'10px 18px',
										fontSize:'14px',
                                        background: activeCategory === category 
                                            ? 'linear-gradient(135deg, var(--accent-color) 0%, #B8935D 100%)' 
                                            : 'rgba(201, 169, 110, 0.05)',
                                        color: activeCategory === category ? '#000' : 'var(--accent-color)',
                                        border: `2px solid ${activeCategory === category ? 'var(--accent-color)' : 'rgba(201, 169, 110, 0.2)'}`,
                                        borderRadius: '50px',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.5px',
                                        boxShadow: activeCategory === category 
                                            ? '0 4px 15px rgba(201, 169, 110, 0.3)' 
                                            : 'none',
                                        transform: activeCategory === category ? 'translateY(-2px)' : 'translateY(0)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeCategory !== category) {
                                            e.target.style.background = 'rgba(201, 169, 110, 0.15)';
                                            e.target.style.borderColor = 'var(--accent-color)';
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(201, 169, 110, 0.2)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeCategory !== category) {
                                            e.target.style.background = 'rgba(201, 169, 110, 0.05)';
                                            e.target.style.borderColor = 'rgba(201, 169, 110, 0.2)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        {/* Article Count */}
                        <div style={{ 
                            marginTop: '30px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 20px',
                            background: 'rgba(201, 169, 110, 0.1)',
                            borderRadius: '25px',
                            border: '1px solid rgba(201, 169, 110, 0.2)'
                        }}>
                            <span style={{ color: 'var(--accent-color)' }}>{renderIcon('book', 20)}</span>
                            <span style={{ fontSize: '15px', color: 'var(--accent-color)', fontWeight: '500' }}>
                                {filteredBlogs.length} {filteredBlogs.length === 1 ? 'Article' : 'Articles'}
                            </span>
                        </div>
                    </div>

                    {/* Blog Grid */}
                    <div className="blog-grid" style={{ gap: '40px' }}>
                        {loading ? (
                            <div style={{ 
                                textAlign: 'center', 
                                gridColumn: '1 / -1',
                                padding: '80px 20px',
                                color: 'var(--accent-color)'
                            }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    border: '3px solid rgba(201, 169, 110, 0.2)',
                                    borderTop: '3px solid var(--accent-color)',
                                    borderRadius: '50%',
                                    margin: '0 auto 20px',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                <p style={{ fontSize: '16px' }}>Loading amazing content...</p>
                            </div>
                        ) : filteredBlogs.length > 0 ? (
                            filteredBlogs.map(blog => (
                                <Link to={`/blog/${blog.slug}`} key={blog._id} className="blog-card blog-list-card">
                                    <div className="blog-list-image-wrapper" style={{ 
                                        width: '100%',
                                        height:'clamp(180px,30vw,280px)',
                                        overflow: 'hidden',
                                        borderRadius: '16px 16px 0 0',
                                        position: 'relative'
                                    }}>
                                        <img 
                                            src={blog.featuredImage} 
                                            alt={blog.title}
                                            className="blog-list-image"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                                display: 'block'
                                            }}
                                        />
                                        <div className="blog-list-overlay" style={{
                                            position: 'absolute',
                                            top: '0',
                                            left: '0',
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%)',
                                            opacity: '0',
                                            transition: 'opacity 0.4s ease',
                                            pointerEvents: 'none'
                                        }}></div>
                                        <div style={{
                                            position: 'absolute',
                                            top: '20px',
                                            right: '20px',
                                            padding: '8px 18px',
                                            background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935D 100%)',
                                            color: '#000',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            borderRadius: '25px',
                                            textTransform: 'capitalize',
                                            letterSpacing: '0.5px',
                                            boxShadow: '0 4px 15px rgba(201, 169, 110, 0.4)'
                                        }}>
                                            {blog.category}
                                        </div>
                                    </div>
                                    <div className="blog-card-content" style={{ padding: '35px 30px 30px' }}>
                                        <h3 style={{ 
                                            fontSize: '24px',
                                            fontWeight: '400',
                                            marginBottom: '15px',
                                            color: 'var(--text-light)',
                                            lineHeight: '1.4',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            {blog.title}
                                        </h3>
                                        <p style={{ 
                                            fontSize: '15px',
                                            lineHeight: '1.8',
                                            color: 'rgba(245, 241, 237, 0.7)',
                                            marginBottom: '25px',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            {blog.excerpt}
                                        </p>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            paddingTop: '20px',
                                            borderTop: '1px solid rgba(201, 169, 110, 0.1)'
                                        }}>
                                            <div className="blog-card-meta" style={{ 
                                                display: 'flex',
                                                gap: '15px',
                                                fontSize: '13px',
                                                color: 'rgba(245, 241, 237, 0.5)',
                                                alignItems: 'center'
                                            }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                                    </svg>
                                                    {new Date(blog.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                                <span>•</span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                    {blog.views || 0}
                                                </span>
                                            </div>
                                            <span style={{
                                                color: 'var(--accent-color)',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px'
                                            }}>
                                                Read More
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                    <polyline points="12 5 19 12 12 19"></polyline>
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div style={{ 
                                textAlign: 'center', 
                                gridColumn: '1 / -1',
                                padding: '80px 20px',
                                color: 'rgba(245, 241, 237, 0.6)'
                            }}>
                                <div style={{ marginBottom: '20px', color: 'var(--accent-color)', display: 'flex', justifyContent: 'center' }}>
                                    {renderIcon('inbox', 64)}
                                </div>
                                <h3 style={{ fontSize: '24px', color: 'var(--text-light)', marginBottom: '10px' }}>
                                    No Articles Found
                                </h3>
                                <p style={{ fontSize: '16px' }}>
                                    Try selecting a different category to explore more content.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Blog Stats & Highlights Section */}
            <section style={{ 
                background: 'linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 100%)',
                padding: '80px 5%',
                borderTop: '1px solid rgba(201, 169, 110, 0.1)',
                borderBottom: '1px solid rgba(201, 169, 110, 0.1)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-10%',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(201, 169, 110, 0.08) 0%, transparent 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}></div>

                <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <h2 style={{
                            fontSize: 'clamp(28px, 4vw, 38px)',
                            fontWeight: '300',
                            color: 'var(--text-light)',
                            marginBottom: '15px'
                        }}>
                            Why Read Our <span style={{ color: 'var(--accent-color)' }}>Design Blog</span>?
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'rgba(245, 241, 237, 0.6)',
                            maxWidth: '550px',
                            margin: '0 auto'
                        }}>
                            Expert insights from 25+ years of design excellence
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '40px'
                    }}>
                        <div className="blog-stat-item" style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'rgba(201, 169, 110, 0.05)',
                            borderRadius: '20px',
                            border: '1px solid rgba(201, 169, 110, 0.15)',
                            transition: 'all 0.4s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.05)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ marginBottom: '15px', color: 'var(--accent-color)', display: 'flex', justifyContent: 'center' }}>
                                {renderIcon('edit', 48)}
                            </div>
                            <h3 style={{
                                fontSize: '32px',
                                fontWeight: '600',
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>
                                {blogs.length}+
                            </h3>
                            <p style={{
                                fontSize: '16px',
                                color: 'rgba(245, 241, 237, 0.7)',
                                fontWeight: '500'
                            }}>
                                Expert Articles
                            </p>
                        </div>

                        <div className="blog-stat-item" style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'rgba(201, 169, 110, 0.05)',
                            borderRadius: '20px',
                            border: '1px solid rgba(201, 169, 110, 0.15)',
                            transition: 'all 0.4s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.05)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ marginBottom: '15px', color: 'var(--accent-color)', display: 'flex', justifyContent: 'center' }}>
                                {renderIcon('palette', 48)}
                            </div>
                            <h3 style={{
                                fontSize: '32px',
                                fontWeight: '600',
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>
                                8
                            </h3>
                            <p style={{
                                fontSize: '16px',
                                color: 'rgba(245, 241, 237, 0.7)',
                                fontWeight: '500'
                            }}>
                                Design Studios
                            </p>
                        </div>

                        <div className="blog-stat-item" style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'rgba(201, 169, 110, 0.05)',
                            borderRadius: '20px',
                            border: '1px solid rgba(201, 169, 110, 0.15)',
                            transition: 'all 0.4s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.05)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ marginBottom: '15px', color: 'var(--accent-color)', display: 'flex', justifyContent: 'center' }}>
                                {renderIcon('lightbulb', 48)}
                            </div>
                            <h3 style={{
                                fontSize: '32px',
                                fontWeight: '600',
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>
                                25+
                            </h3>
                            <p style={{
                                fontSize: '16px',
                                color: 'rgba(245, 241, 237, 0.7)',
                                fontWeight: '500'
                            }}>
                                Years Experience
                            </p>
                        </div>

                        <div className="blog-stat-item" style={{
                            textAlign: 'center',
                            padding: '40px 30px',
                            background: 'rgba(201, 169, 110, 0.05)',
                            borderRadius: '20px',
                            border: '1px solid rgba(201, 169, 110, 0.15)',
                            transition: 'all 0.4s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-10px)';
                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(201, 169, 110, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.05)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                            <div style={{ marginBottom: '15px', color: 'var(--accent-color)', display: 'flex', justifyContent: 'center' }}>
                                {renderIcon('trophy', 48)}
                            </div>
                            <h3 style={{
                                fontSize: '32px',
                                fontWeight: '600',
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>
                                48+
                            </h3>
                            <p style={{
                                fontSize: '16px',
                                color: 'rgba(245, 241, 237, 0.7)',
                                fontWeight: '500'
                            }}>
                                Segments
                            </p>
                        </div>
                    </div>
                </div>
            </section>


            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{ width: '180px', marginBottom: '20px', opacity: 0.8 }} />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
            </footer>
        </div>
    );
};

export default BlogListPage;

